import {
  Clock3,
  Edit3,
  PauseCircle,
  PlayCircle,
  Power,
  Star,
  UtensilsCrossed,
} from 'lucide-react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import { formatMoney } from '../../../../utils/decimalValue';

import { MenuItem } from '../types';

interface MenuCardProps {
  item: MenuItem;
  onEdit: (item: MenuItem) => void;
  onToggleAvailable: (item: MenuItem) => void;
  onToggleActive: (item: MenuItem) => void;
}

const MenuCard = ({
  item,
  onEdit,
  onToggleAvailable,
  onToggleActive,
}: MenuCardProps) => {
  return (
    <article
      className={`
        overflow-hidden
        rounded-xl
        border
        border-stroke
        bg-white
        shadow-card-2
        transition-all
        duration-200
        hover:-translate-y-0.5
        hover:shadow-card
        dark:border-strokedark
        dark:bg-boxdark
        dark:shadow-none
        ${item.active ? '' : 'opacity-65'}
      `}
    >
      <div className="relative flex h-32 items-center justify-center border-b border-stroke bg-gradient-to-br from-primary/5 to-primary/15 dark:border-strokedark dark:from-primary/10 dark:to-primary/20">
        <span className="rounded-xl border border-primary/15 bg-white/70 p-4 text-primary/60 backdrop-blur-sm dark:border-primary/25 dark:bg-boxdark/70">
          <UtensilsCrossed className="h-9 w-9" />
        </span>

        {item.featured && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-warning/15 px-2.5 py-1 text-xs font-semibold text-warning">
            <Star className="h-3.5 w-3.5 fill-current" />
            Destacado
          </span>
        )}

        <span
          className={`
            absolute
            right-3
            top-3
            rounded-full
            px-2.5
            py-1
            text-xs
            font-semibold
            ${
              item.effectiveAvailable
                ? 'bg-success/10 text-success dark:bg-success/15'
                : 'bg-danger/10 text-danger dark:bg-danger/15'
            }
          `}
        >
          {item.effectiveAvailable ? 'Disponible' : 'No disponible'}
        </span>
      </div>

      <div className="p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          {item.categoryName} · {item.code}
        </p>

        <div className="mt-2 flex items-start justify-between gap-3">
          <h3 className="min-w-0 font-semibold text-black dark:text-white">
            {item.displayName || item.productName}
          </h3>

          <span className="shrink-0 text-lg font-bold text-primary">
            {formatMoney(item.productPrice)}
          </span>
        </div>

        <p className="mt-3 line-clamp-2 min-h-[40px] text-sm text-bodydark2 dark:text-bodydark">
          {item.description || item.productName}
        </p>

        <div className="mt-3 min-h-[24px]">
          {item.preparationTimeMinutes !== null && (
            <p className="inline-flex items-center gap-1.5 rounded-lg bg-gray-2 px-2.5 py-1 text-xs text-bodydark2 dark:bg-meta-4 dark:text-bodydark">
              <Clock3 className="h-3.5 w-3.5" />
              {item.preparationTimeMinutes} min
            </p>
          )}
          {item.preparationStationName && (
            <p className="ml-2 inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1 text-xs text-primary">
              {item.preparationStationName}
            </p>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 border-t border-stroke pt-4 dark:border-strokedark">
          <ActionButton
            label="Editar"
            icon={<Edit3 className="h-4 w-4" />}
            variant="secondary"
            size="sm"
            fullWidth
            onClick={() => onEdit(item)}
          />

          <ActionButton
            label={item.available ? 'Pausar' : 'Habilitar'}
            icon={
              item.available ? (
                <PauseCircle className="h-4 w-4" />
              ) : (
                <PlayCircle className="h-4 w-4" />
              )
            }
            variant={item.available ? 'warning' : 'success'}
            size="sm"
            fullWidth
            disabled={!item.active}
            onClick={() => onToggleAvailable(item)}
          />

          <ActionButton
            label={item.active ? 'Desactivar' : 'Reactivar'}
            icon={<Power className="h-4 w-4" />}
            variant={item.active ? 'danger' : 'success'}
            size="sm"
            fullWidth
            onClick={() => onToggleActive(item)}
            className="col-span-2"
          />
        </div>
      </div>
    </article>
  );
};

export default MenuCard;
